/**
 * Exceptions and variances, as the client sees them.
 *
 * An exception is a documented, time-limited, approved deviation from a
 * policy: "this unit may do X differently until this date, because Y, with
 * these controls in place". A variance is the same thing named the way some
 * accreditors name it; a waiver relieves a requirement altogether. The
 * record is what a surveyor is shown when practice does not match the
 * policy on the shelf.
 *
 * The lifecycle is long because the risk is real: draft → pending approval
 * (a chain of named people, in order) → approved → active → periodically
 * under review → expired → renewed, or revoked at any point. Shapes mirror
 * functions/src/exceptionManagement.
 */

import { hasAtLeast, type Role } from '../../types/roles';

export const EXCEPTION_STATUSES = [
  'draft', 'pending_approval', 'approved', 'active', 'under_review',
  'expired', 'revoked', 'rejected', 'renewal_pending',
] as const;
export type ExceptionStatus = (typeof EXCEPTION_STATUSES)[number];

export const STATUS_LABEL: Record<ExceptionStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  active: 'Active',
  under_review: 'Under review',
  expired: 'Expired',
  revoked: 'Revoked',
  rejected: 'Rejected',
  renewal_pending: 'Renewal pending',
};

export type Tone = 'neutral' | 'info' | 'ok' | 'warn' | 'danger';

export const STATUS_TONE: Record<ExceptionStatus, Tone> = {
  draft: 'neutral',
  pending_approval: 'warn',
  approved: 'info',
  active: 'ok',
  under_review: 'warn',
  expired: 'danger',
  revoked: 'neutral',
  rejected: 'danger',
  renewal_pending: 'warn',
};

export const EXCEPTION_TYPES = ['exception', 'variance', 'waiver'] as const;
export type ExceptionType = (typeof EXCEPTION_TYPES)[number];
export const TYPE_LABEL: Record<ExceptionType, string> = {
  exception: 'Exception', variance: 'Variance', waiver: 'Waiver',
};

export const RISK_LEVELS = ['low', 'medium', 'high', 'critical'] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];
export const RISK_LABEL: Record<RiskLevel, string> = {
  low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical',
};
export const RISK_TONE: Record<RiskLevel, Tone> = {
  low: 'ok', medium: 'info', high: 'warn', critical: 'danger',
};

/** Why the policy cannot be followed as written; what a quality council reports on. */
export const REASON_CATEGORIES = [
  'equipment', 'staffing', 'supply_shortage', 'construction', 'regulatory_change', 'patient_specific', 'technology', 'other',
] as const;
export type ReasonCategory = (typeof REASON_CATEGORIES)[number];
export const REASON_LABEL: Record<ReasonCategory, string> = {
  equipment: 'Equipment out of service',
  staffing: 'Staffing',
  supply_shortage: 'Supply shortage',
  construction: 'Construction or renovation',
  regulatory_change: 'Regulatory change',
  patient_specific: 'Patient-specific',
  technology: 'Technology or system',
  other: 'Other',
};

export const CHAIN_ROLES = ['reviewer', 'approver', 'admin'] as const;
export type ChainRole = (typeof CHAIN_ROLES)[number];
export const CHAIN_ROLE_LABEL: Record<ChainRole, string> = {
  reviewer: 'Reviewer', approver: 'Approver', admin: 'Administrator',
};

export const REVIEW_OUTCOMES = ['continued', 'escalated', 'revoked', 'renewed'] as const;
export type ReviewOutcome = (typeof REVIEW_OUTCOMES)[number];
export const OUTCOME_LABEL: Record<ReviewOutcome, string> = {
  continued: 'Continue', escalated: 'Escalate', revoked: 'Revoke', renewed: 'Renew',
};
export const OUTCOME_HINT: Record<ReviewOutcome, string> = {
  continued: 'The exception stays active as it is.',
  escalated: 'Flag it for further review; it stays active meanwhile.',
  revoked: 'End the exception now.',
  renewed: 'It needs formal renewal with a new expiration and approval.',
};

function member<T extends readonly string[]>(list: T, v: unknown, fallback: T[number]): T[number] {
  return (list as readonly unknown[]).includes(v) ? (v as T[number]) : fallback;
}
export const toStatus = (v: unknown) => member(EXCEPTION_STATUSES, v, 'draft');
export const toType = (v: unknown) => member(EXCEPTION_TYPES, v, 'exception');
export const toRisk = (v: unknown) => member(RISK_LEVELS, v, 'medium');
export const toReason = (v: unknown) => member(REASON_CATEGORIES, v, 'other');
export const toChainRole = (v: unknown) => member(CHAIN_ROLES, v, 'reviewer');
export const toOutcome = (v: unknown) => member(REVIEW_OUTCOMES, v, 'continued');

export interface ApprovalStep {
  stepOrder: number;
  approverUid: string;
  approverEmail: string | null;
  approverDisplayName: string | null;
  role: ChainRole;
  decision: 'approved' | 'rejected' | null;
  decidedAt: string | null;
  comments: string | null;
}

export interface ExceptionRecord {
  id: string;
  title: string;
  description: string;
  exceptionType: ExceptionType;
  riskLevel: RiskLevel;
  status: ExceptionStatus;
  policyId: string;
  policyVersionId: string;
  policyVersionNumber: number;
  policyTitle: string;
  justification: string;
  mitigatingControls: string[];
  impactAssessment: string | null;
  affectedDepartmentIds: string[];
  requestorUid: string;
  requestorEmail: string | null;
  requestorDisplayName: string | null;
  approvalChain: ApprovalStep[];
  currentApprovalStep: number;
  effectiveDate: string | null;
  expirationDate: string | null;
  reviewIntervalDays: number | null;
  nextReviewDate: string | null;
  policyVersionAtApproval: number | null;
  policyVersionChanged: boolean;
  renewalCount: number;
  maxRenewals: number | null;
  conditions: string | null;
  reasonCategory: ReasonCategory;
  oversightCommittee: string | null;
  version: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ExceptionVersion {
  id: string;
  version: number;
  trigger: string;
  createdAt: string | null;
}

export interface ExceptionEvent {
  id: string;
  action: string;
  actorEmail: string | null;
  details: Record<string, unknown>;
  createdAt: string | null;
}

export interface ExceptionReview {
  id: string;
  reviewerEmail: string | null;
  reviewDate: string | null;
  outcome: ReviewOutcome;
  riskLevelAtReview: RiskLevel;
  riskLevelChanged: boolean;
  newRiskLevel: RiskLevel | null;
  findings: string | null;
  conditions: string | null;
}

export const EVENT_LABEL: Record<string, string> = {
  created: 'Created',
  updated: 'Edited',
  submitted_for_approval: 'Submitted for approval',
  approval_step_approved: 'Approval step approved',
  approval_step_rejected: 'Approval step rejected',
  recalled: 'Recalled',
  fully_approved: 'Fully approved',
  activated: 'Activated',
  expired: 'Expired',
  revoked: 'Revoked',
  review_initiated: 'Review started',
  review_completed: 'Review completed',
  renewal_requested: 'Renewal requested',
  renewal_submitted: 'Renewal submitted',
  risk_level_changed: 'Risk level changed',
  policy_version_mismatch_detected: 'Policy changed underneath this exception',
  policy_version_mismatch_acknowledged: 'Policy change acknowledged',
  conditions_updated: 'Conditions updated',
  deleted: 'Deleted',
};
export const eventLabel = (a: string) => EVENT_LABEL[a] ?? a.replace(/_/g, ' ');

/** What the create and update callables take, less the ids. */
export interface ExceptionFields {
  title: string;
  exceptionType: ExceptionType;
  riskLevel: RiskLevel;
  policyId: string;
  policyVersionId: string;
  policyVersionNumber: number | null;
  policyTitle: string;
  description: string;
  justification: string;
  mitigatingControls: string[];
  impactAssessment: string;
  conditions: string;
  affectedDepartmentIds: string[];
  /** "YYYY-MM-DD"; required. */
  expirationDate: string;
  /** Empty means none. */
  reviewIntervalDays: string;
  maxRenewals: string;
  reasonCategory: ReasonCategory;
  oversightCommittee: string;
}

export const EMPTY_FIELDS: ExceptionFields = {
  title: '', exceptionType: 'exception', riskLevel: 'medium',
  policyId: '', policyVersionId: '', policyVersionNumber: null, policyTitle: '',
  description: '', justification: '', mitigatingControls: [],
  impactAssessment: '', conditions: '', affectedDepartmentIds: [],
  expirationDate: '', reviewIntervalDays: '', maxRenewals: '',
  reasonCategory: 'other', oversightCommittee: '',
};

/**
 * The approval chain a risk level demands. Mirrors
 * validateApprovalChainForRisk on the backend, so the builder can say what
 * is missing before the submit is refused.
 */
export function chainProblem(risk: RiskLevel, chain: readonly { role: ChainRole }[]): string | null {
  if (chain.length === 0) return 'Add at least one approver.';
  if (risk === 'medium' && !chain.some((s) => s.role === 'approver' || s.role === 'admin')) {
    return 'A medium-risk exception needs at least one approver or administrator in the chain.';
  }
  if (risk === 'high' || risk === 'critical') {
    if (chain.length < 2) return `A ${risk}-risk exception needs at least two approvers.`;
    if (!chain.some((s) => s.role === 'admin')) return `A ${risk}-risk exception needs an administrator in the chain.`;
  }
  return null;
}

/**
 * Which actions this person may take now. Mirrors each callable's status
 * and role checks — and, for an approval step, its identity check: only the
 * person named on the current step may decide it, whatever their role.
 */
export function exceptionActions(exc: ExceptionRecord, roles: readonly Role[], uid: string | null) {
  const author = hasAtLeast(roles, 'author');
  const reviewer = hasAtLeast(roles, 'reviewer');
  const admin = hasAtLeast(roles, 'admin');
  const s = exc.status;
  const step = exc.approvalChain[exc.currentApprovalStep];
  const mine = s === 'pending_approval' && !!step && !!uid && step.approverUid === uid;
  const renewable = exc.maxRenewals === null || exc.renewalCount < exc.maxRenewals;
  return {
    edit: author && s === 'draft',
    submit: author && s === 'draft',
    delete: admin && s === 'draft',
    decideStep: mine,
    recall: author && s === 'pending_approval',
    activate: admin && s === 'approved',
    initiateReview: reviewer && s === 'active',
    completeReview: reviewer && s === 'under_review',
    revoke: admin && (s === 'active' || s === 'under_review'),
    requestRenewal: author && s === 'expired' && renewable,
    submitRenewal: author && s === 'renewal_pending',
    acknowledgeMismatch: admin && exc.policyVersionChanged,
  };
}
